import { CameraPhotos } from '@EcamModel/model';
import { appConfig } from '@configs/index';
import { Box, Stack, Typography } from '@mui/material';
import { formatDateByTimeZone } from 'src/helpers';
import { useEffect, useMemo, useRef, useState } from 'react';
import { bboxToRect, PcVehicleContext, findPcVehicleInPhoto } from '../utils/PcVehicleBbox';
import { convertToCropArea } from './PopUpPlateEditing';
import { useImageDimensions } from '@pages/cameras/live-photos/components/PreviewImage';
import color from '@theme/Colors';
import { loadingShimmerSx } from '@theme/LoadingShimmer';

export type EvidenceTag = { label: string; color: string };
type GeoZonePoint = { x: number; y: number; geoZoneId: number | undefined };

type Props = {
    item: CameraPhotos;
    evidenceTags: EvidenceTag[];
    timezone: string;
    prevGeoZones?: GeoZonePoint[][];
    geoZoneDisplayWidth: number;
    vehicleContext: PcVehicleContext;
    highlighted?: boolean;
    focused?: boolean;
    onClick: () => void;
    onContextMenu?: (e: React.MouseEvent) => void;
};

// ─── single thumbnail in the strip ───────────────────────────────────────────
export default function LivePhotoThumbnail({
    item,
    evidenceTags,
    timezone,
    prevGeoZones,
    geoZoneDisplayWidth,
    vehicleContext,
    highlighted,
    focused,
    onClick,
    onContextMenu,
}: Props) {
    const blobUrl = `${appConfig.gateway.blobURL}${item.BlobName}`;
    const { image } = useImageDimensions(item.BlobName);

    const vbW = geoZoneDisplayWidth || 1920;
    const vbH = vbW * (9 / 16);
    const rootW = image?.width || 1920;
    const displayScaleSize = rootW / vbW;

    // Only the PC's vehicle is drawn (same rule as ReviewDetail) — other cars in frame get no box.
    const pcVehicle = useMemo(() => findPcVehicleInPhoto(item, vehicleContext), [item, vehicleContext]);

    const displayCropAreas = image
        ? (pcVehicle ? [pcVehicle.vehicle] : []).flatMap((v) => {
              const ca = convertToCropArea({
                  X1: v.X1 / displayScaleSize,
                  X2: v.X2 / displayScaleSize,
                  X3: v.X3 / displayScaleSize,
                  X4: v.X4 / displayScaleSize,
                  Y1: v.Y1 / displayScaleSize,
                  Y2: v.Y2 / displayScaleSize,
                  Y3: v.Y3 / displayScaleSize,
                  Y4: v.Y4 / displayScaleSize,
              });
              return ca ? [ca] : [];
          })
        : [];

    const vehicleBBoxes = pcVehicle?.vehicleBbox
        ? [bboxToRect(pcVehicle.vehicleBbox)].map((r) => ({
              x: r.x / displayScaleSize,
              y: r.y / displayScaleSize,
              width: r.width / displayScaleSize,
              height: r.height / displayScaleSize,
          }))
        : [];

    const [imgLoaded, setImgLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    // handle cached images where onLoad fires before React attaches the handler
    useEffect(() => {
        const img = imgRef.current;
        if (img && img.complete && img.naturalWidth > 0) {
            setImgLoaded(true);
        }
    }, [blobUrl]);

    return (
        <Box
            onClick={onClick}
            onContextMenu={onContextMenu}
            sx={{
                position: 'relative',
                width: 340,
                minWidth: 340,
                aspectRatio: '16/9',
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                border: focused
                    ? '3px solid #00e5ff'
                    : highlighted
                      ? '2px solid #00e5ff'
                      : evidenceTags.length
                        ? `2px solid ${evidenceTags[0].color}`
                        : '2px solid transparent',
                boxShadow: focused
                    ? '0 0 12px 3px rgba(0,229,255,0.6)'
                    : highlighted
                      ? '0 0 8px 2px rgba(0,229,255,0.5)'
                      : 'none',
                flexShrink: 0,
                bgcolor: '#e0e0e0',
                ...(!imgLoaded && loadingShimmerSx),
                '&:hover': { opacity: 0.85 },
                transition: 'opacity 0.23s, border-color 0.3s, box-shadow 0.3s',
            }}
        >
            <img
                ref={imgRef}
                src={blobUrl}
                alt=""
                onLoad={() => setImgLoaded(true)}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: imgLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                }}
            />
            {/* overlays */}
            {image && (
                <svg
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        overflow: 'hidden',
                    }}
                    viewBox={`0 0 ${vbW} ${vbH}`}
                    preserveAspectRatio="xMidYMid slice"
                >
                    {/* Geo-zone polygons */}
                    {prevGeoZones
                        ?.filter((f) => f[0]?.geoZoneId)
                        .map((frame, fi) => {
                            if (!frame?.length) return null;
                            const pts = frame.map((p) => `${p.x},${p.y}`).join(' ');
                            return (
                                <polyline
                                    key={`gz-${fi}`}
                                    points={pts}
                                    stroke={color.accentPink}
                                    strokeWidth={5}
                                    strokeDasharray="20 10"
                                     fill="rgba(0, 0, 0, 0.12)"
                                    strokeLinejoin="round"
                                />
                            );
                        })}

                    {displayCropAreas.map((ca, i) => (
                        <rect
                            key={`crop-${i}`}
                            x={ca.x}
                            y={ca.y}
                            width={ca.width}
                            height={ca.height}
                            stroke="red"
                            strokeWidth={4}
                            fill="none"
                        />
                    ))}

                    {vehicleBBoxes.map((bbox, i) => (
                        <rect
                            key={`bbox-${i}`}
                            x={bbox.x}
                            y={bbox.y}
                            width={bbox.width}
                            height={bbox.height}
                            stroke={evidenceTags[0]?.color ?? color.mint}
                            strokeWidth={6}
                            fill="none"
                        />
                    ))}
                </svg>
            )}

            {/* Evidence tags */}
            {evidenceTags.length > 0 && (
                <Stack
                    direction="row"
                    spacing={0.3}
                    sx={{ position: 'absolute', top: 4, left: 4, flexWrap: 'wrap', gap: 0.3 }}
                >
                    {evidenceTags.map((tag, i) => (
                        <Box
                            key={i}
                            sx={{
                                bgcolor: tag.color,
                                color: color.white,
                                fontSize: 9,
                                fontWeight: 600,
                                px: 0.5,
                                py: 0.15,
                                borderRadius: 0.5,
                                lineHeight: 1.2,
                            }}
                        >
                            {tag.label}
                        </Box>
                    ))}
                </Stack>
            )}

            {/* Capture time + Plate(s) */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
                    px: 0.5,
                    py: 0.3,
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="#fff" fontSize={10} noWrap>
                        {formatDateByTimeZone(item.CaptureAt, timezone)}
                    </Typography>
                    {(() => {
                        const plateText = item.CameraPhotoVehicles
                            ?.map((v) => v.AiPlateCandidate?.text)
                            .filter((t): t is string => !!t && t !== 'NaN')
                            .join(' · ');
                        return plateText ? (
                            <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                    color: '#FFFF00',
                                    fontSize: 10,
                                    fontWeight: 550,
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    px: 0.5,
                                    borderRadius: 0.5,
                                    maxWidth: '55%',
                                }}
                            >
                                {plateText}
                            </Typography>
                        ) : null;
                    })()}
                </Stack>
            </Box>
        </Box>
    );
}
